import { useEffect, useState } from 'react';
import { BookOpen, Clock, Play } from 'lucide-react';

/**
 * Modal popup for choosing play mode (apprentissage vs classique).
 * Shown after user clicks a quiz card/button, before launching the quiz.
 *
 * Optionnel : `counts` = [{ value, label? }] affiche un sélecteur de nombre de
 * questions au-dessus des modes (utilisé par « Révisions du jour »). `onSelect`
 * reçoit alors (mode, count).
 */
export default function PlayModePicker({ title, note, resumes = null, counts = null, defaultCount = null, onSelect, onCancel }) {
    const hasResumes = Array.isArray(resumes) && resumes.length > 0;
    const hasCounts = Array.isArray(counts) && counts.length > 1;
    const [count, setCount] = useState(() => {
        if (!hasCounts) return null;
        if (defaultCount && counts.some(c => c.value === defaultCount)) return defaultCount;
        return counts[Math.min(1, counts.length - 1)].value; // 2e option par défaut (≈ standard)
    });

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    return (
        <div className="play-mode-overlay" onClick={(e) => e.target === e.currentTarget && onCancel()}>
            <div className="play-mode-modal">
                <h3 className="play-mode-title">{title || 'Choisir le mode'}</h3>
                {note && <p style={{ fontSize: '0.82em', color: 'var(--text-muted)', margin: '-4px 0 14px', textAlign: 'center' }}>{note}</p>}

                {hasResumes && (
                    <>
                        <div style={{ fontSize: '0.78em', color: 'var(--text-muted)', marginBottom: 6 }}>
                            {resumes.length > 1 ? `Séances en cours (${resumes.length})` : 'Séance en cours'}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                            {resumes.map(r => (
                                <button key={r.id} type="button" onClick={r.onResume}
                                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 10, cursor: 'pointer', textAlign: 'left', border: '1px solid var(--primary)', background: 'color-mix(in srgb, var(--primary) 10%, transparent)', color: 'var(--text)' }}>
                                    <Play size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                                    <span style={{ flex: 1, minWidth: 0 }}>
                                        <strong style={{ display: 'block', color: 'var(--primary)' }}>Reprendre · {r.label}</strong>
                                        {r.date && <span style={{ fontSize: '0.76em', color: 'var(--text-muted)' }}>démarrée le {r.date}</span>}
                                    </span>
                                </button>
                            ))}
                        </div>
                        <div style={{ textAlign: 'center', fontSize: '0.78em', color: 'var(--text-muted)', margin: '0 0 14px' }}>ou démarre une nouvelle séance</div>
                    </>
                )}

                {hasCounts && (
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: '0.78em', color: 'var(--text-muted)', textAlign: 'center', marginBottom: 6 }}>Nombre de questions</div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                            {counts.map(c => {
                                const active = count === c.value;
                                return (
                                    <button key={c.value} type="button" onClick={() => setCount(c.value)}
                                        style={{
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                                            minWidth: 60, padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
                                            border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                                            background: active ? 'color-mix(in srgb, var(--primary) 14%, transparent)' : 'transparent',
                                            color: active ? 'var(--primary)' : 'var(--text)',
                                        }}>
                                        <strong style={{ fontSize: '1.05em' }}>{c.value}</strong>
                                        {c.label && <span style={{ fontSize: '0.7em', opacity: 0.8 }}>{c.label}</span>}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="play-mode-options">
                    <button
                        type="button"
                        className="play-mode-btn play-mode-btn--apprentissage"
                        onClick={() => onSelect('apprentissage', count)}
                    >
                        <BookOpen size={22} strokeWidth={2} />
                        <strong>Apprentissage</strong>
                        <span className="play-mode-desc">Correction immédiate après chaque question</span>
                    </button>
                    <button
                        type="button"
                        className="play-mode-btn play-mode-btn--quiz"
                        onClick={() => onSelect('quiz', count)}
                    >
                        <Clock size={22} strokeWidth={2} />
                        <strong>Classique</strong>
                        <span className="play-mode-desc">Toutes les questions puis résultat global</span>
                    </button>
                </div>
                <button className="play-mode-cancel" onClick={onCancel}>Annuler</button>
            </div>
        </div>
    );
}
